import React, { useState, useEffect, useCallback } from 'react';
import monitoringService from '../services/monitoringService';
import { Card, StatCard } from '../components/common';

const SecurityDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await monitoringService.getSecurityDashboard();
      if (result.success) setData(result.data);
    } catch (err) {
      console.error('Security dashboard error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        <p className="text-gray-600 text-sm">보안 대시보드 로딩 중...</p>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-16 text-gray-500">데이터를 불러올 수 없습니다.</div>;
  }

  const criticalAlerts = data.alertStats?.find(a => a.severity === 'critical');
  const warningAlerts = data.alertStats?.find(a => a.severity === 'warning');
  const totalDown = data.downEvents?.reduce((sum, e) => sum + e.down_count, 0) || 0;

  return (
    <div className="flex flex-col gap-6 max-w-[1200px] mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-[28px] font-bold text-gray-900 m-0">보안 대시보드</h1>
        <button onClick={loadData} className="text-sm text-blue-500 hover:text-blue-700 bg-blue-50 px-4 py-2 rounded-lg">새로고침</button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <StatCard
          icon="🔴"
          value={criticalAlerts?.unresolved || 0}
          label="미해결 심각 알림"
          variant={criticalAlerts?.unresolved > 0 ? 'error' : ''}
        />
        <StatCard
          icon="🟡"
          value={warningAlerts?.unresolved || 0}
          label="미해결 경고"
          variant={warningAlerts?.unresolved > 0 ? 'warning' : ''}
        />
        <StatCard
          icon="🔒"
          value={data.sslWarnings?.length || 0}
          label="SSL 만료 임박"
          variant={data.sslWarnings?.length > 0 ? 'warning' : ''}
        />
        <StatCard
          icon="📡"
          value={totalDown}
          label="서비스 다운 (24h)"
          variant={totalDown > 0 ? 'error' : ''}
        />
      </div>

      {/* SSL Warnings */}
      {data.sslWarnings && data.sslWarnings.length > 0 && (
        <Card title="SSL 인증서 경고">
          <div className="space-y-2">
            {data.sslWarnings.map((ssl, i) => (
              <div key={i} className="flex items-center justify-between py-3 px-4 bg-amber-50 border border-amber-200 rounded-lg">
                <span className="font-medium text-amber-900">{ssl.domain}</span>
                <div className="text-right">
                  <span className="text-amber-700 font-semibold">{ssl.days_remaining}일 남음</span>
                  <span className="text-xs text-amber-600 block">만료: {new Date(ssl.valid_to).toLocaleDateString('ko-KR')}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Service Down Events */}
      {data.downEvents && data.downEvents.length > 0 && (
        <Card title="서비스 다운 이벤트 (24시간)">
          <div className="space-y-2">
            {data.downEvents.map((event, i) => (
              <div key={i} className="flex items-center justify-between py-3 px-4 bg-red-50 border border-red-200 rounded-lg">
                <span className="font-medium text-red-900">{event.service_name}</span>
                <span className="text-red-700 font-semibold">{event.down_count}회 다운</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent Admin Actions */}
      <Card title="최근 관리자 활동">
        {data.recentActions && data.recentActions.length > 0 ? (
          <div className="space-y-2">
            {data.recentActions.map((action, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-gray-600">{action.user_email}</span>
                  <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs font-mono">{action.action}</span>
                </div>
                <div className="text-right text-xs text-gray-500">
                  {action.target_type && <span className="mr-2">{action.target_type}: {action.target_id}</span>}
                  {new Date(action.created_at).toLocaleString('ko-KR')}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm text-center py-8">최근 활동이 없습니다.</p>
        )}
      </Card>
    </div>
  );
};

export default SecurityDashboard;
